import {
  parseStructuredJsonPayload,
  serializeStructuredJsonPayload
} from '../../shared/design-system/structured-json-form.utils';

export type OfferingProfileFieldType = 'string' | 'number' | 'boolean' | 'select' | 'textarea' | 'offering-multi';

export interface OfferingProfileFieldOption {
  value: string;
  labelKey: string;
}

export interface OfferingProfileFieldDef {
  key: string;
  type: OfferingProfileFieldType;
  required?: boolean;
  options?: OfferingProfileFieldOption[];
}

/** Profile type values (labels come from i18n: catalog.profiles.types.{value}). */
export const OFFERING_PROFILE_TYPE_VALUES = [
  'PhysicalGoodProfile',
  'ServiceProfile',
  'HospitalityProfile',
  'RentalProfile',
  'DigitalProfile',
  'SubscriptionProfile',
  'AssetProfile',
  'PreciousMetalProfile',
  'CapitalMarketInstrumentProfile',
  'BrokerageServiceProfile',
  'InsuranceProductProfile',
  'FxExchangeProfile'
] as const;

export type OfferingProfileTypeValue = (typeof OFFERING_PROFILE_TYPE_VALUES)[number];

export function profileTypeLabelKey(profileType: string): string {
  return `catalog.profiles.types.${profileType}`;
}

export function fieldLabelKey(fieldKey: string): string {
  return `catalog.profiles.fields.${fieldKey}`;
}

const yesNo: OfferingProfileFieldOption[] = [
  { value: 'true', labelKey: 'catalog.profiles.options.yes' },
  { value: 'false', labelKey: 'catalog.profiles.options.no' }
];

export const OFFERING_PROFILE_SCHEMAS: Record<string, OfferingProfileFieldDef[]> = {
  PhysicalGoodProfile: [
    { key: 'sku', type: 'string', required: true },
    {
      key: 'inventory_mode',
      type: 'select',
      required: true,
      options: [
        { value: 'Tracked', labelKey: 'catalog.profiles.options.inventoryMode.Tracked' },
        { value: 'Untracked', labelKey: 'catalog.profiles.options.inventoryMode.Untracked' }
      ]
    },
    { key: 'technical_spec', type: 'textarea', required: true },
    { key: 'unit_of_measure_code', type: 'string', required: true },
    { key: 'default_storage_location', type: 'string', required: true },
    { key: 'component_offering_ids', type: 'offering-multi' },
    { key: 'profile_base_price', type: 'number' },
    { key: 'insurance_required', type: 'boolean' },
    { key: 'insurance_coverage_notes', type: 'textarea' },
    { key: 'packaging_type', type: 'string', required: true },
    { key: 'packaging_quantity', type: 'number' },
    { key: 'requires_shipping', type: 'boolean' },
    { key: 'weight_kg', type: 'number' },
    { key: 'barcode', type: 'string' }
  ],
  ServiceProfile: [
    { key: 'service_type', type: 'string', required: true },
    { key: 'duration_minutes', type: 'number', required: true },
    { key: 'capacity', type: 'number' },
    { key: 'booking_required', type: 'boolean' }
  ],
  HospitalityProfile: [
    { key: 'capacity', type: 'number', required: true },
    { key: 'amenities', type: 'textarea' },
    { key: 'check_in_time', type: 'string' },
    { key: 'check_out_time', type: 'string' }
  ],
  RentalProfile: [
    {
      key: 'period_unit',
      type: 'select',
      required: true,
      options: [
        { value: 'Day', labelKey: 'catalog.profiles.options.periodUnit.Day' },
        { value: 'Week', labelKey: 'catalog.profiles.options.periodUnit.Week' },
        { value: 'Month', labelKey: 'catalog.profiles.options.periodUnit.Month' }
      ]
    },
    { key: 'deposit_amount', type: 'number', required: true },
    { key: 'min_rental_period', type: 'number' }
  ],
  DigitalProfile: [
    { key: 'license_rule', type: 'string', required: true },
    { key: 'access_duration_days', type: 'number' },
    { key: 'download_limit', type: 'number' }
  ],
  SubscriptionProfile: [
    {
      key: 'billing_period',
      type: 'select',
      required: true,
      options: [
        { value: 'Monthly', labelKey: 'catalog.profiles.options.billingPeriod.Monthly' },
        { value: 'Yearly', labelKey: 'catalog.profiles.options.billingPeriod.Yearly' }
      ]
    },
    { key: 'trial_days', type: 'number' },
    { key: 'auto_renew', type: 'boolean' }
  ],
  AssetProfile: [
    { key: 'asset_tag', type: 'string' },
    { key: 'depreciation_method', type: 'string' },
    { key: 'useful_life_months', type: 'number' }
  ],
  PreciousMetalProfile: [
    { key: 'purity', type: 'number', required: true },
    { key: 'karat', type: 'number', required: true },
    { key: 'weight_unit', type: 'string', required: true },
    { key: 'net_weight', type: 'number', required: true },
    { key: 'mint_year', type: 'number' },
    { key: 'serial_or_batch', type: 'string' },
    { key: 'vault_required', type: 'boolean' },
    { key: 'premium_formula', type: 'string', required: true },
    { key: 'buy_spread', type: 'number', required: true },
    { key: 'sell_spread', type: 'number', required: true }
  ],
  CapitalMarketInstrumentProfile: [
    { key: 'instrument_type', type: 'string', required: true },
    { key: 'ticker', type: 'string', required: true },
    { key: 'isin', type: 'string', required: true },
    { key: 'market', type: 'string', required: true },
    { key: 'settlement_cycle', type: 'string', required: true },
    { key: 'tradable_sessions', type: 'string' },
    { key: 'risk_level', type: 'string', required: true },
    { key: 'issuer', type: 'string', required: true }
  ],
  BrokerageServiceProfile: [
    {
      key: 'service_mode',
      type: 'select',
      required: true,
      options: [
        { value: 'execution', labelKey: 'catalog.profiles.options.serviceMode.execution' },
        { value: 'advisory', labelKey: 'catalog.profiles.options.serviceMode.advisory' },
        { value: 'discretionary', labelKey: 'catalog.profiles.options.serviceMode.discretionary' }
      ]
    },
    { key: 'commission_model', type: 'string', required: true },
    { key: 'fee_floor', type: 'number', required: true },
    { key: 'fee_cap', type: 'number', required: true },
    { key: 'kyc_required', type: 'select', required: true, options: yesNo },
    { key: 'suitability_required', type: 'select', required: true, options: yesNo }
  ],
  InsuranceProductProfile: [
    { key: 'policy_type', type: 'string', required: true },
    { key: 'coverage_scope', type: 'string', required: true },
    { key: 'sum_insured', type: 'number', required: true },
    { key: 'premium_model', type: 'string', required: true },
    { key: 'deductible', type: 'number' },
    { key: 'waiting_period', type: 'number' },
    { key: 'claim_workflow_type', type: 'string', required: true },
    { key: 'reinsurance_flag', type: 'boolean' }
  ],
  FxExchangeProfile: [
    { key: 'base_currency', type: 'string', required: true },
    { key: 'quote_currency', type: 'string', required: true },
    { key: 'rate_source', type: 'string', required: true },
    { key: 'spread_policy', type: 'string', required: true },
    { key: 'slippage_tolerance', type: 'number' },
    { key: 'daily_limit', type: 'number', required: true },
    { key: 'compliance_ruleset', type: 'string', required: true },
    { key: 'sanction_screening_required', type: 'boolean' }
  ]
};

export function getProfileSchema(profileType: string): OfferingProfileFieldDef[] {
  return OFFERING_PROFILE_SCHEMAS[profileType] ?? [];
}

export function parseProfilePayload(payloadJson: string): Record<string, unknown> {
  return parseStructuredJsonPayload(payloadJson);
}

export function serializeProfilePayload(model: Record<string, unknown>): string {
  return JSON.stringify(JSON.parse(serializeStructuredJsonPayload(model)), null, 2);
}

export function defaultProfileModel(profileType: string): Record<string, unknown> {
  const schema = getProfileSchema(profileType);
  const model: Record<string, unknown> = {};
  for (const field of schema) {
    if (field.type === 'boolean') {
      model[field.key] = false;
    } else if (field.type === 'offering-multi') {
      model[field.key] = [];
    } else if (field.type === 'number') {
      model[field.key] = null;
    } else if (field.type === 'select' && field.options?.length) {
      model[field.key] = field.options[0].value;
    } else {
      model[field.key] = '';
    }
  }
  return model;
}

export function mergeProfileModel(
  profileType: string,
  existing: Record<string, unknown>
): Record<string, unknown> {
  const defaults = defaultProfileModel(profileType);
  const merged = { ...defaults, ...existing };
  for (const field of getProfileSchema(profileType)) {
    if (field.type === 'boolean' && typeof merged[field.key] !== 'boolean') {
      const raw = merged[field.key];
      merged[field.key] = raw === true || raw === 'true';
    }
    if (field.type === 'offering-multi') {
      const raw = merged[field.key];
      if (Array.isArray(raw)) {
        merged[field.key] = raw.filter(item => typeof item === 'string' && item.trim().length > 0);
      } else if (typeof raw === 'string' && raw.trim()) {
        merged[field.key] = raw.split(/[,;\n\r]+/).map(item => item.trim()).filter(Boolean);
      } else {
        merged[field.key] = [];
      }
    }
    if (field.type === 'select' && (field.key === 'kyc_required' || field.key === 'suitability_required')) {
      const raw = merged[field.key];
      if (typeof raw === 'boolean') {
        merged[field.key] = raw ? 'true' : 'false';
      }
    }
    if (field.type === 'number' && merged[field.key] !== null && merged[field.key] !== undefined && merged[field.key] !== '') {
      const numeric = Number(merged[field.key]);
      if (!Number.isNaN(numeric)) {
        merged[field.key] = numeric;
      }
    }
  }
  return merged;
}
